import { useEffect, useMemo, useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import { useIsBrandAdmin } from '../lib/brandAdmin'
import { supabase } from '../lib/supabase'
import { Upload, CheckCircle2, AlertTriangle, FileSpreadsheet, Warehouse, ShoppingBag } from 'lucide-react'
import { format, parseISO } from 'date-fns'
import {
  parseForecast, readShopifyInventoryHeader, buildShopifyInventory, buildAmazonInventory
} from '../lib/planningParse'
import HelpLink from '../components/HelpLink'

// Planning Inputs — the three uploads that feed the replenishment planner:
//   1. Sales forecast (Sales Forecast tab layout)  -> forecast_imports + sales_forecasts
//   2. Shopify inventory export (warehouse)        -> on-hand corrections + inventory_snapshots
//   3. Amazon FBA Inventory report                 -> on-hand corrections + inventory_snapshots

const TABS = [
  { key: 'forecast', label: 'Sales Forecast', icon: FileSpreadsheet },
  { key: 'shopify', label: 'Warehouse (Shopify)', icon: Warehouse },
  { key: 'amazon', label: 'FBA (Amazon)', icon: ShoppingBag },
]

const today = () => format(new Date(), 'yyyy-MM-dd')
const fmtNum = n => (n === null || n === undefined ? '—' : Number(n).toLocaleString(undefined, { maximumFractionDigits: 1 }))
const fmtDate = d => (d ? format(parseISO(String(d).slice(0, 10)), 'MMM d, yyyy') : '—')
const fmtMonth = d => format(parseISO(d), 'MMM yy')

function Steps({ steps }) {
  return (
    <ol style={{ margin: 0, paddingLeft: '18px', display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '13px', color: 'var(--text-2)', lineHeight: 1.5 }}>
      {steps.map((s, i) => <li key={i} dangerouslySetInnerHTML={{ __html: s }} />)}
    </ol>
  )
}

function FilePick({ onFile, label = 'Choose CSV file' }) {
  return (
    <label className="btn btn-secondary" style={{ width: 'fit-content', cursor: 'pointer' }}>
      <Upload size={14} />{label}
      <input type="file" accept=".csv,.txt,.tsv" style={{ display: 'none' }} onChange={e => { const f = e.target.files[0]; e.target.value = ''; if (f) onFile(f) }} />
    </label>
  )
}

function Diff({ v }) {
  if (!v) return <span style={{ color: 'var(--text-3)' }}>0</span>
  return <span style={{ color: v > 0 ? 'var(--success)' : 'var(--danger)', fontWeight: 500 }}>{v > 0 ? '+' : ''}{fmtNum(v)}</span>
}

export default function PlanningInputs() {
  const { activeBrand, user } = useAuth()
  const [tab, setTab] = useState('forecast')
  const [products, setProducts] = useState([])
  const [locations, setLocations] = useState([])
  const [imports, setImports] = useState([])
  const [snapshots, setSnapshots] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => { if (activeBrand) load() }, [activeBrand?.id])

  async function load() {
    setLoading(true)
    const [p, l, fi, sn] = await Promise.all([
      supabase.from('products').select('id, sku, name, asin, product_type').eq('brand_id', activeBrand.id).is('deleted_at', null),
      supabase.from('locations').select('id, name, type, is_active').eq('brand_id', activeBrand.id).eq('is_active', true).order('name'),
      supabase.from('forecast_imports').select('*').eq('brand_id', activeBrand.id).order('created_at', { ascending: false }).limit(15),
      supabase.from('inventory_snapshots').select('*').eq('brand_id', activeBrand.id).order('created_at', { ascending: false }).limit(30),
    ])
    setProducts(p.data || [])
    setLocations(l.data || [])
    setImports(fi.data || [])
    setSnapshots(sn.data || [])
    setLoading(false)
  }

  const activeForecast = imports.find(i => i.is_active)
  const latestSnap = locId => snapshots.find(s => s.location_id === locId)
  const warehouseLocs = locations.filter(l => l.type !== 'amazon_fba')
  const fbaLoc = locations.find(l => l.type === 'amazon_fba')

  if (!activeBrand) return null
  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Planning Inputs<HelpLink section="planning-inputs" /></h1>
          <div style={{ fontSize: '13px', color: 'var(--text-3)', marginTop: '4px' }}>Upload the forecast and inventory exports that drive replenishment planning.</div>
        </div>
      </div>

      {/* Status of the three inputs */}
      <div className="stat-grid" style={{ marginBottom: '20px' }}>
        <div className="stat-card">
          <div className="stat-label">Active forecast</div>
          <div style={{ fontSize: '15px', fontWeight: 600 }}>{activeForecast ? fmtDate(activeForecast.created_at) : 'None uploaded'}</div>
          <div className="stat-sub">{activeForecast ? `${activeForecast.file_name || 'Upload'} · ${fmtNum(activeForecast.row_count)} lines` : 'Upload on the Sales Forecast tab'}</div>
        </div>
        {[...warehouseLocs, ...(fbaLoc ? [fbaLoc] : [])].map(l => {
          const s = latestSnap(l.id)
          return (
            <div className="stat-card" key={l.id}>
              <div className="stat-label">{l.name} inventory as of</div>
              <div style={{ fontSize: '15px', fontWeight: 600 }}>{s ? fmtDate(s.as_of_date) : 'No upload yet'}</div>
              <div className="stat-sub">{s ? `${s.file_name || ''} · uploaded ${fmtDate(s.created_at)}` : '—'}</div>
            </div>
          )
        })}
      </div>

      <div style={{ display: 'flex', gap: '4px', borderBottom: '1px solid var(--border)', marginBottom: '20px' }}>
        {TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px', background: 'none', border: 'none', borderBottom: `2px solid ${tab === key ? 'var(--accent)' : 'transparent'}`, color: tab === key ? 'var(--accent)' : 'var(--text-3)', fontFamily: 'var(--font)', fontSize: '13px', fontWeight: 500, cursor: 'pointer', marginBottom: '-1px' }}>
            <Icon size={14} />{label}
          </button>
        ))}
      </div>

      {loading ? <div style={{ padding: '40px', textAlign: 'center' }}><div className="spinner" style={{ margin: '0 auto' }} /></div> : (
        <>
          {tab === 'forecast' && <ForecastTab brand={activeBrand} products={products} imports={imports} reload={load} />}
          {tab === 'shopify' && <ShopifyTab brand={activeBrand} user={user} products={products} locations={warehouseLocs} reload={load} />}
          {tab === 'amazon' && <AmazonTab brand={activeBrand} user={user} products={products} fbaLoc={fbaLoc} reload={load} />}
        </>
      )}
    </div>
  )
}

// ── 1. Sales forecast ─────────────────────────────────────────────────────────
function ForecastTab({ brand, products, imports, reload }) {
  const isBrandAdmin = useIsBrandAdmin()
  const [file, setFile] = useState(null)
  const [parsed, setParsed] = useState(null)
  const [saving, setSaving] = useState(false)
  const [result, setResult] = useState(null)

  async function onFile(f) {
    setFile(f); setResult(null)
    setParsed(parseForecast(await f.text(), products))
  }

  // Preview: SKU x channel totals for the first 6 months
  const preview = useMemo(() => {
    if (!parsed?.lines) return null
    const months = parsed.months.slice(0, 6)
    const rows = {}
    parsed.lines.forEach(l => {
      const k = `${l.sku}|${l.channel}`
      rows[k] = rows[k] || { sku: l.sku, channel: l.channel, matched: !!l.product_id, byMonth: {}, total: 0 }
      rows[k].total += l.units
      if (months.includes(l.month)) rows[k].byMonth[l.month] = (rows[k].byMonth[l.month] || 0) + l.units
    })
    const list = Object.values(rows).filter(r => r.total !== 0).sort((a, b) => a.sku.localeCompare(b.sku) || a.channel.localeCompare(b.channel))
    return { months, list }
  }, [parsed])

  async function activate(id) {
    await supabase.from('forecast_imports').update({ is_active: false }).eq('brand_id', brand.id).eq('is_active', true)
    await supabase.from('forecast_imports').update({ is_active: true }).eq('id', id)
    await reload()
  }

  async function save() {
    setSaving(true); setResult(null)
    const lines = parsed.lines.filter(l => l.units !== 0)
    // 1. create the import (inactive until all lines are in)
    const { data: imp, error: e1 } = await supabase.from('forecast_imports')
      .insert({ brand_id: brand.id, file_name: file?.name, row_count: lines.length, is_active: false })
      .select().single()
    if (e1) { setResult({ error: e1.message }); setSaving(false); return }
    // 2. insert lines in chunks
    for (let i = 0; i < lines.length; i += 500) {
      const chunk = lines.slice(i, i + 500).map(l => ({ brand_id: brand.id, import_id: imp.id, product_id: l.product_id, sku: l.sku, channel: l.channel, month: l.month, units: l.units }))
      const { error } = await supabase.from('sales_forecasts').insert(chunk)
      if (error) {
        await supabase.from('forecast_imports').delete().eq('id', imp.id)
        setResult({ error: 'Saving forecast lines failed: ' + error.message }); setSaving(false); return
      }
    }
    // 3. switch the active forecast
    await activate(imp.id)
    setResult({ ok: true, lines: lines.length })
    setParsed(null); setFile(null); setSaving(false)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div className="card" style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <Steps steps={[
          'Open the forecast Google Sheet and go to the <strong>Sales Forecast</strong> tab',
          '<strong>File → Download → Comma-separated values (.csv)</strong> (this downloads the current tab only)',
          'Upload it below. Only rows with Channel = <strong>Amazon</strong>, <strong>Direct</strong> or <strong>Other</strong> are read — the Total section is ignored',
          'Review the preview, then click <strong>Save as active forecast</strong>. Earlier forecasts stay in the history below',
        ]} />
        <FilePick onFile={onFile} label={file ? `Replace file (${file.name})` : 'Choose forecast CSV'} />
      </div>

      {result?.ok && <div className="alert alert-success"><CheckCircle2 size={14} /> Forecast saved and set as active ({fmtNum(result.lines)} lines).</div>}
      {(result?.error || parsed?.error) && <div className="alert alert-danger"><AlertTriangle size={14} /> {result?.error || parsed?.error}</div>}

      {parsed?.lines && (
        <div className="card">
          <div style={{ padding: '14px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap', borderBottom: '1px solid var(--border)' }}>
            <div style={{ fontSize: '13px', color: 'var(--text-2)' }}>
              <strong>{preview.list.length}</strong> SKU/channel rows · {fmtMonth(parsed.months[0])} → {fmtMonth(parsed.months[parsed.months.length - 1])} ({parsed.months.length} months)
            </div>
            <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save as active forecast'}</button>
          </div>
          {Object.keys(parsed.fixedSkus).length > 0 && (
            <div className="alert alert-warning" style={{ margin: '12px 16px 0' }}>
              <AlertTriangle size={14} /> Repaired SKUs a spreadsheet had turned into dates: {Object.entries(parsed.fixedSkus).map(([a, b]) => `${a} → ${b}`).join(', ')}
            </div>
          )}
          {parsed.unmatched.length > 0 && (
            <div className="alert alert-warning" style={{ margin: '12px 16px 0' }}>
              <AlertTriangle size={14} /> Not in the catalog (saved, but the planner skips them until the SKU is added): {parsed.unmatched.map(u => `${u.sku} (${fmtNum(u.units)} units)`).join(', ')}
            </div>
          )}
          <div className="table-wrap" style={{ maxHeight: '480px', overflowY: 'auto' }}>
            <table>
              <thead><tr><th>SKU</th><th>Channel</th>{preview.months.map(m => <th key={m} style={{ textAlign: 'right' }}>{fmtMonth(m)}</th>)}<th style={{ textAlign: 'right' }}>All months</th></tr></thead>
              <tbody>
                {preview.list.map(r => (
                  <tr key={r.sku + r.channel}>
                    <td style={{ fontFamily: 'var(--mono)', fontSize: '12px' }}>{r.sku}{!r.matched && <span className="badge badge-warning" style={{ marginLeft: '6px', fontSize: '10px' }}>not in catalog</span>}</td>
                    <td style={{ textTransform: 'capitalize' }}>{r.channel}</td>
                    {preview.months.map(m => <td key={m} style={{ textAlign: 'right' }}>{fmtNum(r.byMonth[m] || 0)}</td>)}
                    <td style={{ textAlign: 'right', fontWeight: 500 }}>{fmtNum(r.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="card">
        <div style={{ padding: '14px 16px', fontSize: '13px', fontWeight: 600, borderBottom: '1px solid var(--border)' }}>Forecast history</div>
        {imports.length === 0 ? <div className="empty-state"><p>No forecasts uploaded yet</p></div> : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Uploaded</th><th>File</th><th style={{ textAlign: 'right' }}>Lines</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {imports.map(i => (
                  <tr key={i.id}>
                    <td>{fmtDate(i.created_at)}</td>
                    <td>{i.file_name || '—'}</td>
                    <td style={{ textAlign: 'right' }}>{fmtNum(i.row_count)}</td>
                    <td>{i.is_active ? <span className="badge badge-success">Active</span> : <span className="badge badge-neutral">Previous</span>}</td>
                    <td>{!i.is_active && !isBrandAdmin && <button className="btn btn-ghost btn-sm" onClick={() => activate(i.id)}>Make active</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

// Shared: current on-hand for one location, keyed by product_id
async function loadOnHand(brandId, locationId) {
  const { data } = await supabase.from('inventory_on_hand').select('product_id, on_hand').eq('brand_id', brandId).eq('location_id', locationId)
  const m = {}
  ;(data || []).forEach(r => { m[r.product_id] = Number(r.on_hand) || 0 })
  return m
}

// Shared: write on-hand corrections + log the snapshot
async function applyCorrections({ brand, user, locationId, rows, transactionType, source, notePrefix, snapshotSource, asOf, fileName, inbound }) {
  const tx = rows.filter(r => r.diff !== 0).map(r => ({
    brand_id: brand.id, product_id: r.product_id, location_id: locationId,
    transaction_type: transactionType, quantity: r.diff, source, created_by: user?.id,
    notes: `${notePrefix} as of ${asOf}: file=${r.newQty}, system=${r.ourQty}`
  }))
  for (let i = 0; i < tx.length; i += 200) {
    const { error } = await supabase.from('inventory_transactions').insert(tx.slice(i, i + 200))
    if (error) return { error: `${error.message} (${i} of ${tx.length} changes saved before the error)` }
  }
  const { error: se } = await supabase.from('inventory_snapshots').insert({
    brand_id: brand.id, location_id: locationId, source: snapshotSource, as_of_date: asOf, file_name: fileName, row_count: rows.length,
    ...(inbound ? { inbound } : {})
  })
  if (se) return { error: 'Inventory updated, but logging the snapshot failed: ' + se.message }
  return { corrections: tx.length }
}

function PreviewTable({ rows, extraCols = [], hideZero, setHideZero }) {
  const shown = hideZero ? rows.filter(r => r.diff !== 0 || r.newQty !== 0) : rows
  return (
    <>
      <div style={{ padding: '8px 16px' }}>
        <label style={{ fontSize: '12px', color: 'var(--text-3)', display: 'flex', gap: '6px', alignItems: 'center', cursor: 'pointer' }}>
          <input type="checkbox" checked={hideZero} onChange={e => setHideZero(e.target.checked)} /> Hide SKUs that are zero in both
        </label>
      </div>
      <div className="table-wrap" style={{ maxHeight: '480px', overflowY: 'auto' }}>
        <table>
          <thead><tr><th>SKU</th><th>Name</th>{extraCols.map(c => <th key={c.key} style={{ textAlign: 'right' }}>{c.label}</th>)}<th style={{ textAlign: 'right' }}>New on hand</th><th style={{ textAlign: 'right' }}>In app now</th><th style={{ textAlign: 'right' }}>Change</th></tr></thead>
          <tbody>
            {shown.map(r => (
              <tr key={r.product_id}>
                <td style={{ fontFamily: 'var(--mono)', fontSize: '12px' }}>{r.sku}{r.notInFile && <span className="badge badge-neutral" style={{ marginLeft: '6px', fontSize: '10px' }}>not in file → 0</span>}</td>
                <td style={{ fontSize: '12px', color: 'var(--text-2)', maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</td>
                {extraCols.map(c => <td key={c.key} style={{ textAlign: 'right' }}>{c.render ? c.render(r) : fmtNum(r[c.key])}</td>)}
                <td style={{ textAlign: 'right', fontWeight: 500 }}>{fmtNum(r.newQty)}</td>
                <td style={{ textAlign: 'right', color: 'var(--text-3)' }}>{fmtNum(r.ourQty)}</td>
                <td style={{ textAlign: 'right' }}><Diff v={r.diff} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

// ── 2. Shopify inventory export → warehouse ──────────────────────────────────
function ShopifyTab({ brand, user, products, locations, reload }) {
  const [locationId, setLocationId] = useState('')
  const [file, setFile] = useState(null)
  const [head, setHead] = useState(null)
  const [selectedCols, setSelectedCols] = useState([])
  const [asOf, setAsOf] = useState(today())
  const [onHand, setOnHand] = useState({})
  const [saving, setSaving] = useState(false)
  const [result, setResult] = useState(null)
  const [hideZero, setHideZero] = useState(true)

  useEffect(() => {
    if (!locationId && locations.length) setLocationId((locations.find(l => /wework|manteca/i.test(l.name)) || locations[0]).id)
  }, [locations])
  useEffect(() => { if (locationId) loadOnHand(brand.id, locationId).then(setOnHand) }, [locationId, brand.id])

  async function onFile(f) {
    setFile(f); setResult(null)
    const h = readShopifyInventoryHeader(await f.text())
    setHead(h)
    setSelectedCols(h.locationCols || [])
  }

  const built = useMemo(() => {
    if (!head?.records) return null
    return buildShopifyInventory({ records: head.records, skuCol: head.skuCol, selectedCols, products })
  }, [head, selectedCols, products])

  const rows = useMemo(() => {
    if (!built) return []
    const ids = new Set([...Object.keys(built.totals), ...Object.keys(onHand).filter(id => onHand[id] !== 0)])
    return [...ids].map(id => {
      const p = products.find(x => x.id === id)
      if (!p || p.product_type === 'virtual_bundle') return null
      const newQty = built.totals[id] ?? 0
      const ourQty = onHand[id] || 0
      return { product_id: id, sku: p.sku, name: p.name, newQty, ourQty, diff: newQty - ourQty, notInFile: !(id in built.totals) }
    }).filter(Boolean).sort((a, b) => a.sku.localeCompare(b.sku))
  }, [built, onHand, products])

  async function save() {
    setSaving(true); setResult(null)
    const loc = locations.find(l => l.id === locationId)
    const r = await applyCorrections({ brand, user, locationId, rows, transactionType: 'shopify_correction', source: 'shopify', notePrefix: `Shopify inventory CSV (${selectedCols.join(' + ')})`, snapshotSource: 'shopify_csv', asOf, fileName: file?.name })
    setResult(r.error ? { error: r.error } : { ok: true, corrections: r.corrections, loc: loc?.name })
    if (!r.error) { setHead(null); setFile(null) }
    setOnHand(await loadOnHand(brand.id, locationId))
    await reload(); setSaving(false)
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div className="card" style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <Steps steps={[
          'In <strong>Shopify admin</strong> go to <strong>Products → Inventory</strong>',
          'Click <strong>Export</strong>. Choose <strong>All variants</strong>, inventory state <strong>Available</strong>, and <strong>Plain CSV file</strong>',
          'Upload the file below <strong>without opening it in Excel or Sheets first</strong> (they turn SKUs like 01-05-014 into dates)',
          'Tick the Shopify location columns that belong to this warehouse (normally just <strong>WeWork c/o Bloomi</strong>), check the preview, then save',
        ]} />
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Warehouse in the app</label>
            <select className="select" value={locationId} onChange={e => setLocationId(e.target.value)}>
              {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Inventory as of</label>
            <input className="input" type="date" value={asOf} onChange={e => setAsOf(e.target.value)} />
          </div>
        </div>
        <FilePick onFile={onFile} label={file ? `Replace file (${file.name})` : 'Choose Shopify inventory CSV'} />
      </div>

      {result?.ok && <div className="alert alert-success"><CheckCircle2 size={14} /> {result.loc} updated — {result.corrections} SKU{result.corrections === 1 ? '' : 's'} changed.</div>}
      {(result?.error || head?.error) && <div className="alert alert-danger"><AlertTriangle size={14} /> {result?.error || head?.error}</div>}

      {head?.records && (
        <div className="card">
          <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Shopify locations to include</div>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              {head.locationCols.map(c => (
                <label key={c} style={{ display: 'flex', gap: '6px', alignItems: 'center', fontSize: '13px', cursor: 'pointer' }}>
                  <input type="checkbox" checked={selectedCols.includes(c)} onChange={e => setSelectedCols(s => e.target.checked ? [...s, c] : s.filter(x => x !== c))} />{c}
                </label>
              ))}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <div style={{ fontSize: '13px', color: 'var(--text-2)' }}>{rows.filter(r => r.diff !== 0).length} SKUs will change</div>
              <button className="btn btn-primary" onClick={save} disabled={saving || !selectedCols.length || !locationId}>{saving ? 'Saving…' : 'Update warehouse inventory'}</button>
            </div>
          </div>
          {built && Object.keys(built.fixedSkus).length > 0 && (
            <div className="alert alert-warning" style={{ margin: '12px 16px 0' }}><AlertTriangle size={14} /> Repaired date-mangled SKUs: {Object.entries(built.fixedSkus).map(([a, b]) => `${a} → ${b}`).join(', ')}</div>
          )}
          {built?.unknown.length > 0 && (
            <div className="alert alert-warning" style={{ margin: '12px 16px 0' }}><AlertTriangle size={14} /> Not in the catalog (ignored): {built.unknown.map(u => `${u.sku} (${fmtNum(u.qty)})`).join(', ')}</div>
          )}
          {built?.skippedBundles.length > 0 && (
            <div style={{ margin: '12px 16px 0', fontSize: '12px', color: 'var(--text-3)' }}>Skipped virtual bundles (their stock is their components'): {built.skippedBundles.map(b => b.sku).join(', ')}</div>
          )}
          <PreviewTable rows={rows} hideZero={hideZero} setHideZero={setHideZero} />
        </div>
      )}
    </div>
  )
}

// ── 3. Amazon FBA Inventory report → FBA ─────────────────────────────────────
function AmazonTab({ brand, user, products, fbaLoc, reload }) {
  const [file, setFile] = useState(null)
  const [built, setBuilt] = useState(null)
  const [asOf, setAsOf] = useState(today())
  const [awd, setAwd] = useState({})
  const [onHand, setOnHand] = useState({})
  const [saving, setSaving] = useState(false)
  const [result, setResult] = useState(null)
  const [hideZero, setHideZero] = useState(true)

  useEffect(() => { if (fbaLoc) loadOnHand(brand.id, fbaLoc.id).then(setOnHand) }, [fbaLoc, brand.id])

  async function onFile(f) {
    setFile(f); setResult(null); setAwd({})
    const b = buildAmazonInventory({ text: await f.text(), products })
    setBuilt(b)
    if (b.snapshotDate) setAsOf(b.snapshotDate)
  }

  const rows = useMemo(() => {
    if (!built?.byProduct) return []
    const ids = new Set([...Object.keys(built.byProduct), ...Object.keys(onHand).filter(id => onHand[id] !== 0), ...Object.keys(awd).filter(id => Number(awd[id]))])
    return [...ids].map(id => {
      const p = products.find(x => x.id === id)
      if (!p) return null
      const a = built.byProduct[id] || { available: 0, fcTransfer: 0, fcProcessing: 0, inbound: 0, inShipped: 0, inReceived: 0 }
      const awdQty = Number(awd[id]) || 0
      const newQty = a.available + a.fcTransfer + a.fcProcessing + awdQty
      const ourQty = onHand[id] || 0
      return { product_id: id, sku: p.sku, name: p.name, available: a.available, fcTransfer: a.fcTransfer, fcProcessing: a.fcProcessing, inbound: a.inbound, inShipped: a.inShipped, inReceived: a.inReceived, awdQty, newQty, ourQty, diff: newQty - ourQty, notInFile: !built.byProduct[id] }
    }).filter(Boolean).sort((x, y) => x.sku.localeCompare(y.sku))
  }, [built, onHand, awd, products])

  async function save() {
    setSaving(true); setResult(null)
    // Amazon inbound (shipped to Amazon, not yet checked in) is saved with the snapshot; the Planner counts it as landing ~2 weeks later
    const inbound = {}
    rows.forEach(r => { if (r.inShipped || r.inReceived) inbound[r.product_id] = { shipped: r.inShipped || 0, received: r.inReceived || 0 } })
    const r = await applyCorrections({ brand, user, locationId: fbaLoc.id, rows, transactionType: 'amazon_correction', source: 'amazon_csv', notePrefix: 'Amazon FBA Inventory (available + fc-transfer + FC processing + AWD)', snapshotSource: 'amazon_csv', asOf, fileName: file?.name, inbound })
    setResult(r.error ? { error: r.error } : { ok: true, corrections: r.corrections })
    if (!r.error) { setBuilt(null); setFile(null); setAwd({}) }
    setOnHand(await loadOnHand(brand.id, fbaLoc.id))
    await reload(); setSaving(false)
  }

  if (!fbaLoc) return <div className="alert alert-warning"><AlertTriangle size={14} /> This brand has no active Amazon FBA location.</div>

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div className="card" style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
        <Steps steps={[
          'In <strong>Seller Central</strong> open the menu and go to <strong>Supply Chain → Reports</strong>',
          'In the left sidebar under <strong>Inventory</strong>, click <strong>FBA Inventory</strong> (click <em>Show more…</em> if it isn\'t listed)',
          'Click <strong>Request .csv Download</strong> (.txt also works), wait until the status changes from <em>In Progress</em> to <strong>Download</strong>, then download it. Don\'t open it in Excel first',
          'Upload it below. On hand = <strong>available + fc-transfer + FC processing</strong>, summed per ASIN. Inbound is saved separately for the Planner: <strong>received</strong> units (at Amazon, still checking in) count from this date, <strong>shipped</strong> units count 2 weeks after it, and <strong>working</strong> units (shipment created but not sent) are ignored because the app\'s planned transfer covers them',
          'If you have AWD stock, enter it in the AWD column before saving',
        ]} />
        <div className="form-grid">
          <div className="form-group">
            <label className="form-label">Inventory as of (from the report's snapshot date)</label>
            <input className="input" type="date" value={asOf} onChange={e => setAsOf(e.target.value)} />
          </div>
        </div>
        <FilePick onFile={onFile} label={file ? `Replace file (${file.name})` : 'Choose FBA Inventory CSV'} />
      </div>

      {result?.ok && <div className="alert alert-success"><CheckCircle2 size={14} /> FBA updated — {result.corrections} SKU{result.corrections === 1 ? '' : 's'} changed.</div>}
      {(result?.error || built?.error) && <div className="alert alert-danger"><AlertTriangle size={14} /> {result?.error || built?.error}</div>}

      {built?.byProduct && (
        <div className="card">
          <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <div style={{ fontSize: '13px', color: 'var(--text-2)' }}>{rows.filter(r => r.diff !== 0).length} SKUs will change{!built.hasFcProcessing && ' · this report has no FC Processing column'}</div>
            <button className="btn btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Update FBA inventory'}</button>
          </div>
          {built.unknown.length > 0 && (
            <div className="alert alert-warning" style={{ margin: '12px 16px 0' }}><AlertTriangle size={14} /> ASINs not in the catalog (ignored): {built.unknown.map(u => `${u.asin}${u.sku ? ` / ${u.sku}` : ''} (${fmtNum(u.qty)})`).join(', ')}</div>
          )}
          <PreviewTable rows={rows} hideZero={hideZero} setHideZero={setHideZero} extraCols={[
            { key: 'available', label: 'Available' },
            { key: 'fcTransfer', label: 'FC transfer' },
            { key: 'fcProcessing', label: 'FC processing' },
            { key: 'awdQty', label: 'AWD', render: r => <input className="input" type="number" min="0" style={{ width: '80px', textAlign: 'right', padding: '4px 6px' }} value={awd[r.product_id] ?? ''} placeholder="0" onChange={e => setAwd(a => ({ ...a, [r.product_id]: e.target.value }))} /> },
            { key: 'inbound', label: 'Inbound', render: r => <span style={{ color: 'var(--text-3)' }} title={`${fmtNum(r.inShipped)} shipped + ${fmtNum(r.inReceived)} received`}>{fmtNum(r.inbound)}</span> },
          ]} />
        </div>
      )}
    </div>
  )
}
